/**
 * Helpers shared by every page that saves club settings.
 *
 * The settings save stores only the fields on the server's list. Until
 * 2026-10-08 a field that was not on the list was dropped while the page said
 * "saved". The server now names what it did not store (`not_stored`), and
 * these helpers turn that into words for staff.
 */

/** "fee_cycle_months" -> "fee cycle months" */
const readable = (field: string): string => field.replace(/_/g, " ");

/** The message staff see when a save left something out. Empty when nothing was left out. */
export function describeNotStored(fields: unknown): string {
  if (!Array.isArray(fields)) return "";
  const names = fields.filter((f): f is string => typeof f === "string" && f.length > 0);
  if (names.length === 0) return "";
  const list = names.map(readable).join(", ");
  return names.length === 1
    ? `One setting on this page was NOT saved: ${list}. Everything else was saved. Please let support know.`
    : `${names.length} settings on this page were NOT saved: ${list}. Everything else was saved. Please let support know.`;
}

/**
 * The server's own words for a refused save ("Fee cycle (months) must be a
 * whole number from 1 to 120."), or the page's fallback.
 */
export function settingsSaveErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: unknown; not_stored?: unknown } } })
    ?.response?.data;
  const message = typeof data?.message === "string" ? data.message.trim() : "";
  return message || fallback;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Keep only the fields whose value differs from what the page loaded.
 *
 * Used for settings that change charges, deactivation or the public site: a
 * save must never write a value that is only on the screen because it is the
 * form's starting value. With no baseline (the settings never loaded) nothing
 * is sent.
 */
export function onlyChangedFields(
  current: Record<string, unknown>,
  loaded: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (!loaded) return {};
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(current)) {
    if (!same(current[key], loaded[key])) out[key] = current[key];
  }
  return out;
}
